import React from 'react'

import { IActionType } from '@makyoapp/makyo-common'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { parseLabel } from '@/shared/utils'

import { SPECIAL_NODES } from './constants'
import { WorkflowNodeData } from './types'

// Component to render action descriptions with variable text support
export const ActionDescription: React.FC<Pick<WorkflowNodeData, 'actionValue'>> = ({ actionValue }) => {
  const { dialogs, overlays, pages, menus } = useAppBuilderContext()
  const { tables } = useDbClientContext()

  // Get description from action value settings
  if (!actionValue.settings || Object.keys(actionValue.settings).length === 0) {
    return null
  }

  const settings = actionValue.settings
  const actionType = actionValue.type

  // Handle start-trigger action as a special case (check by node ID)
  if (actionValue.id === SPECIAL_NODES.trigger) {
    return null
  }

  // Special handling for dialog actions
  if (actionType === IActionType.ShowDialog && settings.dialog) {
    const dialogId = settings.dialog
    if (!dialogId || !dialogs) return null

    const dialog = dialogs.find((d) => d.id === dialogId)
    if (!dialog) return dialogId

    return parseLabel(dialog.name)
  }

  // Special handling for conditional actions
  if (actionType === IActionType.ConditionalIf && settings.conditions) {
    const conditions = settings.conditions
    if (conditions.conditions && conditions.conditions.length > 0) {
      const conditionCount = conditions.conditions.length
      return `${conditionCount} condition${conditionCount > 1 ? 's' : ''}`
    }
    return 'No conditions'
  }

  if (actionType === IActionType.ConditionalSwitch && settings.cases) {
    const cases = settings.cases
    if (cases && cases.length > 0) {
      return `${cases.length} case${cases.length > 1 ? 's' : ''}`
    }
    return 'No cases'
  }

  if (actionType === IActionType.OpenOverlay && settings.overlay) {
    const overlayId = settings.overlay
    if (!overlayId || !overlays) return null

    const overlay = overlays.find((o) => o.id === overlayId)
    if (!overlay) return overlayId

    return parseLabel(overlay.title)
  }

  if (actionType === IActionType.GoToScreen) {
    const screenId = settings.screen
    if (!screenId || !pages) return null

    const screen = pages.find((p) => p.id === screenId)
    if (!screen) return screenId

    return parseLabel(screen.title)
  }

  if (actionType === IActionType.AddNewRow) {
    const tableId = settings.tableId
    if (!tableId || !tables) return null

    const table = tables.find((t) => t.id === tableId)
    if (!table) return tableId

    return parseLabel(table.name)
  }

  if (actionType === IActionType.OpenMenu) {
    const menuId = settings.menu
    if (!menuId || !menus) return null

    const menu = menus.find((m) => m.id === menuId)
    if (!menu) return menuId

    return parseLabel(menu.title)
  }

  if (actionType === IActionType.ShowSnackbar) {
    if (typeof settings.value?.value === 'string') {
      return settings.value.value
    }

    return null
  }

  return null
}
